<template>
  <div>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="filteredCategories"
        :items-per-page="10"
        class="elevation-0 custom-data-table"
        :no-data-text="noDataText"
        :height="tableHeight"
        fixed-header
        :loading="tableLoading"
        density="compact"
      >
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center pa-4">
            <span class="text-h5">Medication Categories</span>
            <div class="d-flex align-center">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="outlined"
                density="compact"
                style="width: 350px; margin-right: 16px"
              ></v-text-field>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openCreateDialog"
              >
                Create Category
              </v-btn>
            </div>
          </div>
        </template>

        <template v-slot:[`item.category_name`]="{ item }">
          {{ formatMedicationCategoryName(item.category_name) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            size="small"
            variant="text"
            color="primary"
            @click="editCategory(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            color="error"
            @click="deleteCategory(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <CreateCategoryDialog
      v-model="showCreateDialog"
      @created="handleCategoryCreated"
    />

    <EditCategoryDialog
      v-model="showEditDialog"
      :category="selectedCategory"
      @updated="handleCategoryUpdated"
    />

    <DeleteCategoryDialog
      v-model="showDeleteDialog"
      :category="selectedCategory"
      @deleted="handleCategoryDeleted"
    />
  </div>
</template>

<script>
import CreateCategoryDialog from "../Dialog/CreateCategoryDialog.vue";
import EditCategoryDialog from "../Dialog/EditCategoryDialog.vue";
import DeleteCategoryDialog from "../Dialog/DeleteCategoryDialog.vue";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";

export default {
  name: "MedicationCategoryCard",
  mixins: [GetMedicationCategories],
  components: {
    CreateCategoryDialog,
    EditCategoryDialog,
    DeleteCategoryDialog,
  },
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      tableLoading: false,
      selectedCategory: null,
      selectedIndex: -1,
      search: "",
      windowHeight: window.innerHeight,
      categories: [],
      headers: [
        { title: "Category Name", key: "category_name", sortable: true },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    noDataText() {
      return "No categories found. Click 'Create Category' to add one.";
    },
    tableHeight() {
      return this.windowHeight - 280;
    },
    filteredCategories() {
      if (!this.search) {
        return this.categories;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return this.categories.filter((category) => {
        const name = (formatMedicationCategoryName(category.category_name) || "").toLowerCase();
        return name.includes(searchTerm);
      });
    },
  },
  async mounted() {
    this.updateWindowHeight();
    window.addEventListener("resize", this.updateWindowHeight);
    this.loadCategories();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    formatMedicationCategoryName,
    onTabActivated() {
      this.loadCategories();
    },
    async loadCategories() {
      this.tableLoading = true;
      try {
        await this.fetchMedicationCategories();
        this.categories = this.medicationCategoriesList.map((item) => ({
          category_id: item.medication_category_id,
          category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication categories:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    openCreateDialog() {
      this.showCreateDialog = true;
    },
    async handleCategoryCreated() {
      await this.loadCategories();
    },
    editCategory(category) {
      this.selectedCategory = { ...category };
      this.selectedIndex = this.categories.findIndex(
        (c) => c.category_id === category.category_id
      );
      this.showEditDialog = true;
    },
    async handleCategoryUpdated() {
      await this.loadCategories();
      this.selectedIndex = -1;
      this.selectedCategory = null;
    },
    deleteCategory(category) {
      this.selectedCategory = { ...category };
      this.selectedIndex = this.categories.findIndex(
        (c) => c.category_id === category.category_id
      );
      this.showDeleteDialog = true;
    },
    async handleCategoryDeleted() {
      await this.loadCategories();
      this.selectedIndex = -1;
      this.selectedCategory = null;
    },
  },
};
</script>
